import type { IconName } from '@uniit/ui-kit';
import type { RoleId } from './models/role.model';

export interface NavEntry {
  readonly id: string;
  readonly path: string;
  readonly label: string;
  readonly icon: IconName;
  /** Spec couverte, et version où l'écran est prévu. */
  readonly spec: string;
  readonly available: boolean;
  /**
   * Permission (code `uniit:domaine:action`) qui ouvre l'entrée en mode branché (L3-04). Absente
   * pour les écrans V2 sans backend (achats, catalogue, support…), qui restent donc masqués en mode
   * branché tant que leur module n'existe pas.
   */
  readonly permission?: string;
}

const ENTRIES: readonly NavEntry[] = [
  {
    id: 'dashboard', path: '/tableau-de-bord', label: 'Tableau de bord', icon: 'layout-dashboard', spec: 'SPEC-PIN-006', available: true, permission: 'uniit:dashboard:read',
  },
  {
    id: 'parc', path: '/equipements', label: 'Parc matériel', icon: 'monitor', spec: 'SPEC-PIN-001', available: true, permission: 'uniit:asset:read',
  },
  {
    id: 'monmateriel', path: '/mon-materiel', label: 'Mon matériel', icon: 'notebook', spec: 'SPEC-PIN-001', available: true, permission: 'uniit:asset:read',
  },
  {
    id: 'employes', path: '/employes', label: 'Collaborateurs', icon: 'user', spec: 'SPEC-PIN-008', available: true, permission: 'uniit:collaborator:read',
  },
  {
    id: 'flux', path: '/flux', label: 'Arrivées & départs', icon: 'circle-dot-dashed', spec: 'SPEC-PIN-002', available: true, permission: 'uniit:onboarding:read',
  },
  {
    id: 'packs', path: '/flux/packs', label: 'Packs de dotation', icon: 'package', spec: 'SPEC-PIN-002', available: true, permission: 'uniit:onboarding:manage',
  },
  {
    id: 'commandes', path: '/commandes', label: 'Achats & commandes', icon: 'badge-euro', spec: 'SPEC-PIN-003', available: true,
  },
  {
    id: 'catalogue', path: '/commandes/catalogue', label: 'Catalogue interne', icon: 'star', spec: 'SPEC-PIN-003', available: true,
  },
  {
    id: 'support', path: '/support', label: 'Support & tickets', icon: 'bell', spec: 'SPEC-PIN-005', available: false,
  },
  {
    id: 'integrations', path: '/integrations', label: 'Intégrations SIRH / MDM', icon: 'network', spec: 'SPEC-PIN-004', available: false,
  },
];

/**
 * Entrées visibles en <b>mode branché</b> : celles dont la permission figure dans le token
 * (L3-04). Les écrans V2 sans permission (achats, catalogue) restent masqués. Le contrôle réel est
 * côté serveur (L3-02) ; ceci n'est que le reflet UI.
 */
export function navForPermissions(permissions: ReadonlySet<string>): readonly NavEntry[] {
  return ENTRIES.filter(
    (entry) => entry.available && entry.permission !== undefined && permissions.has(entry.permission),
  );
}

/** Entrées visibles par rôle, reprises de `navIds()` dans la maquette. */
export function navFor(role: RoleId): readonly NavEntry[] {
  // Deux écrans sont réservés à l'Administrateur parc IT : les packs de dotation (SPEC-PIN-002 §6)
  // et, avec le Gestionnaire IT, le catalogue interne (SPEC-PIN-003 §3). Le Service comptable, lui,
  // ne voit que les commandes : il approuve les montants et n'a accès ni au catalogue ni aux paniers.
  const ids =
    role === 'collab'
      ? ['monmateriel', 'support']
      : role === 'manager'
        ? ['dashboard', 'parc', 'employes', 'flux', 'commandes']
        : role === 'compta'
          ? ['dashboard', 'commandes']
          : role === 'gestion'
            ? [
                'dashboard',
                'parc',
                'employes',
                'flux',
                'commandes',
                'catalogue',
                'support',
                'integrations',
              ]
            : [
                'dashboard',
                'parc',
                'employes',
                'flux',
                'packs',
                'commandes',
                'catalogue',
                'support',
                'integrations',
              ];

  return ids.map((id) => ENTRIES.find((entry) => entry.id === id)!);
}

/** Le Manager/RH voit « Parc de mon équipe » là où les autres voient « Vos équipements ». */
export function navLabel(entry: NavEntry, role: RoleId): string {
  return entry.id === 'parc' && role === 'manager' ? 'Parc de mon équipe' : entry.label;
}
